import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Award, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card, EmptyState, fmtCurrency } from "@/components/dash/ui";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { toast } from "sonner";

export default function QuotationCompare() {
  const { rfqId } = useParams();
  const nav = useNavigate();
  const [rfq, setRfq] = React.useState(null);
  const [quotes, setQuotes] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const [r, q] = await Promise.all([
        api.get(`/rfqs/${rfqId}`),
        api.get("/quotations", { params: { rfq_id: rfqId, page_size: 50 } }),
      ]);
      setRfq(r.data); setQuotes(q.data.items);
    } catch { setQuotes([]); }
  }, [rfqId]);
  React.useEffect(() => { load(); }, [load]);

  const award = async (qid) => {
    try { await api.patch(`/quotations/${qid}`, { status: "awarded" }); toast.success("Quotation awarded"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const best = quotes && quotes.length ? Math.min(...quotes.map((q) => q.total || Infinity)) : null;
  const d = (v) => { try { return v ? format(new Date(v), "dd MMM yyyy") : "—"; } catch { return "—"; } };

  const ROWS = [
    ["Total (incl. tax)", (q) => <span className={`font-mono font-semibold ${q.total === best ? "text-emerald-600" : "text-slate-800"}`}>{fmtCurrency(q.total)}{q.total === best && " ★"}</span>],
    ["Base Amount", (q) => <span className="font-mono">{fmtCurrency(q.subtotal)}</span>],
    ["Tax", (q) => `${q.tax || 0}%`],
    ["Delivery Timeline", (q) => q.delivery_timeline || "—"],
    ["Valid Until", (q) => d(q.validity)],
    ["Status", (q) => <StatusBadge status={q.status} />],
    ["Notes", (q) => <span className="text-slate-500">{q.notes || "—"}</span>],
  ];

  return (
    <div>
      <PageHeader title="Quotation Comparison" description={rfq ? rfq.title : "Compare vendor bids side by side"} breadcrumb="Procurement / Compare">
        <Button variant="outline" onClick={() => nav("/app/rfqs")}><ArrowLeft className="h-4 w-4 mr-1.5" />Back to RFQs</Button>
      </PageHeader>
      {quotes === null ? <div className="text-slate-400">Loading…</div> : quotes.length === 0 ? (
        <Card><EmptyState icon="Quote" title="No quotations yet" description="Invited vendors have not submitted quotations for this RFQ yet." /></Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="text-left p-3 text-xs uppercase tracking-wider text-slate-400 font-semibold sticky left-0 bg-white">Criteria</th>
                {quotes.map((q) => (
                  <th key={q.id} className="p-3 text-left min-w-[180px] border-l border-slate-100">
                    <div className="font-display font-semibold text-slate-900">{q.vendor_name}</div>
                    <div className="text-xs text-slate-400 font-normal">{q.title}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([label, render]) => (
                <tr key={label} className="border-t border-slate-100">
                  <td className="p-3 font-medium text-slate-600 sticky left-0 bg-white">{label}</td>
                  {quotes.map((q) => <td key={q.id} className="p-3 border-l border-slate-100">{render(q)}</td>)}
                </tr>
              ))}
              <tr className="border-t border-slate-100">
                <td className="p-3 sticky left-0 bg-white" />
                {quotes.map((q) => (
                  <td key={q.id} className="p-3 border-l border-slate-100">
                    {q.status !== "awarded" ? (
                      <Button size="sm" className="bg-vs-royal text-white" onClick={() => award(q.id)} data-testid={`compare-award-${q.id}`}><Award className="h-3.5 w-3.5 mr-1" />Award</Button>
                    ) : <StatusBadge status="awarded" />}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
